import { useEffect, useRef } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  ogImage,
  noindex = false,
  jsonLd,
}: SEOProps) {
  const jsonLdRef = useRef<HTMLScriptElement | null>(null);

  const siteUrl =
    typeof __VITE_SITE_URL__ !== 'undefined' && __VITE_SITE_URL__
      ? __VITE_SITE_URL__.replace(/\/+$/, '')
      : 'https://mulebuyspreadsheets.motorcycles';

  const fullCanonical = canonicalPath ? `${siteUrl}${canonicalPath}` : siteUrl;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow';
    setMeta('robots', robotsContent);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', fullCanonical, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', fullCanonical);

    if (jsonLd) {
      if (jsonLdRef.current) {
        jsonLdRef.current.remove();
      }
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      const payload =
        schemas.length === 1
          ? schemas[0]
          : { '@context': 'https://schema.org', '@graph': schemas };
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(payload);
      document.head.appendChild(script);
      jsonLdRef.current = script;
    }
  }, [title, description, keywords, canonicalPath, ogType, ogImage, noindex, jsonLd, fullCanonical, siteUrl]);

  useEffect(() => {
    return () => {
      if (jsonLdRef.current) {
        jsonLdRef.current.remove();
        jsonLdRef.current = null;
      }
    };
  }, []);

  return null;
}